import {
	ENTRIES_UNDO_SCHEMA_VERSION,
	type EntryUndoHistory,
	type EntryUndoRecord,
	isEntryUndoMetadata,
} from "./types";

const DB_NAME = "hourglass-entry-history";
const DB_VERSION = 1;
const STORE_NAME = "histories";
export const MAX_ENTRY_UNDO_RECORDS = 100;

export function emptyEntryUndoHistory(): EntryUndoHistory {
	return { version: ENTRIES_UNDO_SCHEMA_VERSION, undo: [], redo: [] };
}

function isRecord(value: unknown): value is EntryUndoRecord {
	if (!isEntryUndoMetadata(value)) return false;
	const candidate = value as Partial<EntryUndoRecord>;
	return (
		typeof candidate.id === "string" && typeof candidate.createdAt === "string"
	);
}

export function normalizeEntryUndoHistory(value: unknown): EntryUndoHistory {
	if (!value || typeof value !== "object") return emptyEntryUndoHistory();
	const candidate = value as Partial<EntryUndoHistory>;
	if (candidate.version !== ENTRIES_UNDO_SCHEMA_VERSION)
		return emptyEntryUndoHistory();
	return {
		version: ENTRIES_UNDO_SCHEMA_VERSION,
		undo: Array.isArray(candidate.undo)
			? candidate.undo.filter(isRecord).slice(-MAX_ENTRY_UNDO_RECORDS)
			: [],
		redo: Array.isArray(candidate.redo)
			? candidate.redo.filter(isRecord).slice(-MAX_ENTRY_UNDO_RECORDS)
			: [],
	};
}

function openDatabase(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE_NAME))
				db.createObjectStore(STORE_NAME);
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () =>
			reject(request.error ?? new Error("Unable to open undo history"));
	});
}

export async function readEntryUndoHistory(
	scope: string,
): Promise<EntryUndoHistory> {
	if (!scope || typeof indexedDB === "undefined")
		return emptyEntryUndoHistory();
	try {
		const db = await openDatabase();
		return await new Promise((resolve) => {
			const request = db
				.transaction(STORE_NAME, "readonly")
				.objectStore(STORE_NAME)
				.get(scope);
			request.onsuccess = () => {
				db.close();
				resolve(normalizeEntryUndoHistory(request.result));
			};
			request.onerror = () => {
				db.close();
				resolve(emptyEntryUndoHistory());
			};
		});
	} catch {
		return emptyEntryUndoHistory();
	}
}

export async function updateEntryUndoHistory(
	scope: string,
	updater: (current: EntryUndoHistory) => EntryUndoHistory,
): Promise<EntryUndoHistory> {
	if (!scope || typeof indexedDB === "undefined")
		return emptyEntryUndoHistory();
	let lastError: unknown;
	for (let attempt = 0; attempt < 3; attempt += 1) {
		try {
			const db = await openDatabase();
			return await new Promise((resolve, reject) => {
				const transaction = db.transaction(STORE_NAME, "readwrite");
				const store = transaction.objectStore(STORE_NAME);
				const request = store.get(scope);
				let next = emptyEntryUndoHistory();
				request.onsuccess = () => {
					next = normalizeEntryUndoHistory(
						updater(normalizeEntryUndoHistory(request.result)),
					);
					store.put(next, scope);
				};
				request.onerror = () => transaction.abort();
				transaction.oncomplete = () => {
					db.close();
					resolve(next);
				};
				transaction.onerror = () => {
					db.close();
					reject(
						transaction.error ?? new Error("Unable to update undo history"),
					);
				};
				transaction.onabort = () => {
					db.close();
					reject(
						transaction.error ?? new Error("Unable to update undo history"),
					);
				};
			});
		} catch (error) {
			lastError = error;
		}
	}
	throw lastError ?? new Error("Unable to update undo history");
}

export async function writeEntryUndoHistory(
	scope: string,
	history: EntryUndoHistory,
): Promise<void> {
	if (!scope || typeof indexedDB === "undefined") return;
	const normalized = normalizeEntryUndoHistory(history);
	try {
		const db = await openDatabase();
		await new Promise<void>((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, "readwrite");
			transaction.objectStore(STORE_NAME).put(normalized, scope);
			transaction.oncomplete = () => {
				db.close();
				resolve();
			};
			transaction.onerror = () => {
				db.close();
				reject(transaction.error);
			};
			transaction.onabort = () => {
				db.close();
				reject(transaction.error);
			};
		});
	} catch {
		// Undo persistence is useful but must never block the underlying mutation.
	}
}
